<template>
  <div class="w-full">
    <div class="flex items-center justify-between bg-white">
      <div
        class="flex w-full min-w-max gap-6 border-b border-steel-300 bg-white px-3 pb-4 md:w-max md:border-none"
      >
        <span
          class="cursor-pointer"
          :class="{
            'border-b-2 border-sun-700 text-sun-700': selectedTab === 'Active',
            'text-base text-steel-700': selectedTab !== 'Active',
            'flex-1 text-sm': !isDesktop,
          }"
          @click="emit('handle-tab-selection', 'Active')"
        >
          Active
          <span v-if="truckPostingsLength && truckPostingsLength !== 0">
            ({{ truckPostingsLength }})
          </span>
        </span>

        <span
          v-if="showPotentialCapacityTab"
          class="min-w-max cursor-pointer"
          :class="{
            'border-b-2 border-sun-700 text-sun-700':
              selectedTab === 'Potential',
            'text-base text-steel-700': selectedTab !== 'Potential',
            'flex-1 text-sm': !isDesktop,
          }"
          @click="emit('handle-tab-selection', 'Potential')"
        >
          Suggested
          <span v-if="potentialTrucksLength && potentialTrucksLength !== 0">
            ({{ potentialTrucksLength }})
          </span>
        </span>

        <span
          class="cursor-pointer"
          :class="{
            'border-b-2 border-sun-700 text-sun-700': isExpired,
            'text-base text-steel-700': !isExpired,
            'flex-1 text-sm': !isDesktop,
          }"
          @click="emit('handle-tab-selection', 'Expired')"
        >
          Expired
        </span>
      </div>

      <div
        v-if="canUpdateAutomaticCapacitySettings"
        class="hidden min-w-max items-center pr-4 md:flex"
      >
        <p class="font-normal text-steel-900">
          Automatically post available capacity
        </p>
        <Popper
          placement="bottom-end"
          :hover="true"
          :arrow="true"
          offset-distance="6"
          class="col-span-4 !m-0 !flex h-full items-center py-2"
          :class="{
            'bg-sun-50': selectedPotentialCapacities?.includes(truck?.id),
          }"
        >
          <img
            src="@/assets/question-mark.svg"
            alt="info circle"
            class="mr-2 h-4 w-4"
          />

          <template #content>
            <div
              class="max-w-[350px] rounded-md bg-dark-gray p-3 text-left text-xs text-steel-200"
            >
              <p>
                Automatically post detected capacity to TFX when equipment and
                truck number are provided, so brokers can see your availability.
                Can be turned off anytime.
              </p>
            </div>
          </template>
        </Popper>

        <toggle-icon
          :model-value="automatically_post_capacities"
          :include-icons="true"
          @update:model-value="handleCapacitySettings"
        ></toggle-icon>
      </div>
    </div>

    <div class="relative px-3">
      <div
        v-if="is_truck_posting_loading"
        class="flex h-[calc(100vh-250px)] w-full items-center justify-center rounded-md border border-lighter-gray p-5"
        :class="{
          'shadow-md': isDesktop,
          '': !isDesktop,
        }"
      >
        <div>
          <img
            class="loading m-auto h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading spinner"
          />
          <p class="mt-2 text-base text-steel-900">Loading...</p>
        </div>
      </div>

      <DataTable
        v-else-if="isDesktop"
        :data="trucks"
        :show-pagination="true"
        header-grid-class="grid-cols-21"
        :row-grid-class="
          selectedTab === 'Potential'
            ? 'hover:bg-steel-100 grid-cols-21'
            : !isExpired
              ? 'cursor-pointer hover:bg-steel-100 grid-cols-21'
              : 'grid-cols-21'
        "
        :selected-item="selectedCapacity?.id"
        :show-detail="showCapacityDrawer"
        class="capacity-table !h-[calc(100vh-250px)] shadow-md"
        :pagination="truck_pagination"
        :show-total-results-text="trucks?.length > 0"
        @select-page="onSelectPage"
      >
        <template #header>
          <template v-if="selectedTab === 'Potential'">
            <div class="capacity-checkbox col-span-1 px-3 py-2">
              <CheckboxButton
                v-if="
                  potential_trucks?.length > 0 &&
                  availablePotentialPostings?.length > 0
                "
                item=""
                :active="isAllAvailablePotentialPostingsSelected"
                :handle-select="
                  (e) => emit('handle-select-all-potential-capacities', e)
                "
                class="!h-6 !py-0"
              />
            </div>

            <div class="col-span-4 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">Predicted Truck</p>
            </div>
            <div class="col-span-4 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">Predicted Origin</p>
            </div>
            <div class="col-span-4 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">
                Predicted Equipment
              </p>
            </div>
            <div class="col-span-4 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">Predicted Date</p>
            </div>
            <div class="col-span-4"></div>
          </template>

          <template v-else>
            <div class="col-span-3 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">Truck Number</p>
            </div>
            <div class="col-span-3 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">Origin</p>
            </div>
            <div class="col-span-3 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">Destination</p>
            </div>
            <div class="col-span-3 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">Equipment</p>
            </div>
            <div class="col-span-2 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">Available Date</p>
            </div>
            <div class="col-span-2 flex items-center px-3 py-2">
              <p class="text-xs font-medium text-steel-700">Posted Date</p>
            </div>
            <div
              v-if="!isExpired"
              class="col-span-2 flex items-center px-3 py-2"
            >
              <p class="text-xs font-medium text-steel-700">Expiration Date</p>
            </div>
            <div class="col-span-3"></div>
          </template>
        </template>

        <template
          v-if="trucks?.length > 0"
          #row="{ item: truck }"
        >
          <template v-if="selectedTab === 'Potential'">
            <div
              class="capacity-checkbox col-span-1 flex h-full items-center gap-1 py-2 pl-3"
              :class="{
                'bg-sun-50': selectedPotentialCapacities?.includes(truck?.id),
              }"
            >
              <CheckboxButton
                v-if="
                  truck?.posted_equipment_type &&
                  truck?.posted_equipment_type !== 'other' &&
                  truck?.origin_area_id &&
                  truck?.start_date &&
                  (requireTruckNumberForPosting
                    ? truck?.carrier_vehicle?.truck_number
                    : true)
                "
                item=""
                :active="selectedPotentialCapacities?.includes(truck?.id)"
                :handle-select="
                  () => emit('handle-select-potential-capacity', truck?.id)
                "
                class="!h-6 !py-0"
              />

              <img
                v-else
                class="pl-2"
                src="@/assets/orange-alert.svg"
                alt="orange alert"
              />
            </div>

            <Popper
              v-if="
                requireTruckNumberForPosting &&
                !truck?.carrier_vehicle?.truck_number
              "
              placement="top"
              :hover="true"
              :arrow="true"
              offset-distance="6"
              class="col-span-4 !m-0 !flex h-full items-center py-2"
              :class="{
                'bg-sun-50': selectedPotentialCapacities?.includes(truck?.id),
              }"
            >
              <p class="text-sm font-normal text-steel-900">-</p>
              <p class="text-xs font-light text-steel-900">-</p>

              <template #content>
                <div
                  class="max-w-[180px] rounded-md bg-dark-gray p-2 text-left text-xs text-steel-200"
                >
                  <p>Truck Number is required in order to post.</p>
                </div>
              </template>
            </Popper>

            <div
              v-else
              class="col-span-4 flex flex-col items-start gap-1 py-2 pl-3"
              :class="{
                'bg-sun-50': selectedPotentialCapacities?.includes(truck?.id),
              }"
            >
              <p class="text-sm font-normal text-steel-900">
                {{
                  truck?.carrier_vehicle?.truck_number ||
                  truck?.carrier_vehicle?.id ||
                  '-'
                }}
              </p>
              <p class="text-xs font-light text-steel-900">
                {{ truck.carrier_vehicle?.vehicle?.vin || '-' }}
              </p>
            </div>

            <div
              class="col-span-4 flex flex-col items-start py-2 pl-3"
              :class="{
                'bg-sun-50': selectedPotentialCapacities?.includes(truck?.id),
              }"
            >
              <p class="text-left text-sm font-light text-steel-900">
                {{ truck.origin_search_display_name || '-' }}
              </p>
              <p class="text-sm font-light text-steel-900">
                {{ truck.origin_area?.name || '' }}
              </p>
            </div>

            <div
              v-if="
                truck?.posted_equipment_type &&
                truck?.posted_equipment_type !== 'other'
              "
              class="col-span-4 flex h-full items-center py-2 pl-3 text-start"
              :class="{
                'bg-sun-50': selectedPotentialCapacities?.includes(truck?.id),
              }"
            >
              <p
                class="max-w-max rounded-full bg-ocean-200 px-2 py-[2px] text-sm capitalize text-ocean-700"
              >
                {{ truck.posted_equipment_type?.replace(/_/g, ' ') }}
              </p>
            </div>

            <Popper
              v-else
              placement="top"
              :hover="true"
              :arrow="true"
              offset-distance="6"
              class="col-span-4 !m-0 !flex h-full items-center py-2"
              :class="{
                'bg-sun-50': selectedPotentialCapacities?.includes(truck?.id),
              }"
            >
              <p
                class="ml-2 max-w-max rounded-md bg-steel-1100 px-2 py-[2px] text-sm capitalize text-steel-100"
              >
                Other
              </p>

              <template #content>
                <div
                  class="max-w-[180px] rounded-md bg-dark-gray p-2 text-left text-xs text-steel-200"
                >
                  <p>
                    To post this truck, you must first select the equipment
                    type.
                  </p>
                </div>
              </template>
            </Popper>

            <div
              class="col-span-4 flex h-full items-center py-2 pl-3 text-start"
              :class="{
                'bg-sun-50': selectedPotentialCapacities?.includes(truck?.id),
              }"
            >
              <p class="text-sm font-light text-steel-900">
                {{ dayjs(truck.start_date).format('MM/DD/YYYY') }}
              </p>
            </div>

            <div
              class="col-span-4 flex h-full items-center justify-end px-3 py-2 text-sm font-normal text-steel-900"
              :class="{
                'bg-sun-50': selectedPotentialCapacities?.includes(truck?.id),
              }"
            >
              <ButtonComponent
                class="border border-steel-300"
                @click="handleEditPotentialCapacity(truck)"
              >
                Edit
              </ButtonComponent>
            </div>
          </template>

          <template v-else>
            <div
              class="col-span-3 flex flex-col items-start gap-1 py-2 pl-3"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-sm font-normal text-sun-700 underline">
                Truck:
                {{
                  truck?.carrier_vehicle?.truck_number ||
                  truck?.carrier_vehicle?.id
                }}
              </p>
              <p class="text-xs font-light text-steel-800">
                VIN: {{ truck.carrier_vehicle?.vehicle?.vin }}
              </p>
              <p class="text-left text-xs font-light text-steel-800">
                {{ truck.carrier_vehicle?.vehicle?.model_year }}
                {{ truck.carrier_vehicle?.vehicle?.make }}
                {{ truck.carrier_vehicle?.vehicle?.model }}
              </p>
            </div>

            <div
              class="col-span-3 flex flex-col items-start py-3 pl-3"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-left text-sm font-light text-steel-900">
                {{ truck.origin_search_display_name || '-' }}
              </p>
              <p class="text-sm font-light text-steel-900">
                {{ truck.origin_area?.name || '' }}
              </p>
            </div>

            <div
              class="col-span-3 flex flex-col items-start py-3 pl-3"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-sm font-light text-steel-900">
                {{ truck.destination_search_display_name || '-' }}
              </p>
              <p class="text-sm font-light text-steel-900">
                {{ truck.destination_area?.name || '' }}
              </p>
            </div>

            <div
              class="col-span-3 py-3 pl-3 text-start"
              @click="handleTruckSelection(truck)"
            >
              <p
                class="max-w-max rounded-full bg-ocean-200 px-2 py-[2px] text-sm capitalize text-ocean-700"
              >
                {{ truck.posted_equipment_type?.replace(/_/g, ' ') }}
              </p>
            </div>

            <div
              class="col-span-2 py-3 pl-3 text-start"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-sm font-light text-steel-900">
                {{ dayjs(truck.start_date).format('MMM DD') }}
              </p>
            </div>

            <div
              class="col-span-2 py-3 pl-3 text-start"
              @click="handleTruckSelection(truck)"
            >
              <p class="text-sm font-light text-steel-900">
                {{ dayjs(truck.created_at).format('MMM DD') }}
              </p>
            </div>

            <div
              class="col-span-2 py-3 pl-3 text-start"
              @click="handleTruckSelection(truck)"
            >
              <p
                v-if="truck?.end_date && !isExpired"
                class="text-sm font-light text-steel-900"
              >
                {{ dayjs(truck.end_date).format('MMM DD') }}
              </p>
            </div>

            <div
              v-if="!isExpired"
              class="col-span-3 flex content-center gap-2 p-3 text-sm font-normal text-steel-900"
            >
              <ButtonComponent @click="toggleEditModal(truck)">
                Edit
              </ButtonComponent>
              <ButtonComponent @click="toggleRemoveModal(truck)">
                Remove
              </ButtonComponent>
            </div>

            <div
              v-else
              class="col-span-3 flex justify-end gap-2 p-3 pr-8 text-sm font-normal text-steel-900"
            >
              <ButtonComponent
                v-if="canTruckRepost(truck)"
                @click="toggleRepostTruckModal(truck)"
              >
                Repost
              </ButtonComponent>
            </div>
          </template>
        </template>

        <template #emptyState>
          <div
            v-if="selectedTab === 'Potential'"
            class="mt-6 flex h-full flex-col items-center"
          >
            <img
              src="@/assets/empty-suggested-capacity.svg"
              alt="empty capacity"
            />
            <p class="text-lg font-normal text-steel-700">
              There is no suggested capacity to display
            </p>
          </div>

          <div
            v-else
            class="flex h-full flex-col items-center pt-8"
          >
            <p class="text-lg font-normal text-steel-700">
              {{
                isExpired
                  ? 'You have no expired trucks at this time.'
                  : 'You have no posted trucks at this time.'
              }}
            </p>
          </div>
        </template>
      </DataTable>

      <div
        v-else
        class="pt-4"
      >
        <div
          v-if="is_truck_posting_loading"
          class="min-h-[500px] items-center px-2"
        >
          <img
            class="loading m-auto my-8 h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading"
          />
        </div>
        <div
          v-else-if="trucks?.length > 0"
          class="mb-8 flex flex-col justify-center gap-4"
        >
          <div class="flex items-center justify-between">
            <p class="w-full text-left text-xs font-normal text-steel-800">
              Showing {{ trucks?.length }} of
              {{ truck_pagination?.totalRecords }}
              Truck{{ trucks?.length === 1 ? '' : 's' }}
            </p>

            <div
              v-if="canUpdateAutomaticCapacitySettings"
              class="flex min-w-max items-center gap-2 md:hidden"
            >
              <p class="text-xs font-normal text-steel-800">
                Automatically post
                <br />
                available capacity
              </p>

              <toggle-icon
                :model-value="automatically_post_capacities"
                :include-icons="true"
                @update:model-value="handleCapacitySettings"
              ></toggle-icon>
            </div>
          </div>

          <div class="flex flex-wrap justify-center gap-4">
            <div
              v-for="truck in trucks"
              :key="truck.id"
              class="relative flex w-full max-w-[400px] flex-col rounded-lg border border-steel-200 bg-white p-3 text-left shadow-md"
              :class="{
                'border-2 border-sun-300 shadow-[0_0_1px_0_theme(colors.sun.200)]':
                  selectedPotentialCapacities?.includes(truck?.id),
              }"
              @click="handleSelectMobileSuggested(truck)"
            >
              <p
                class="text-base text-steel-1100"
                :class="{
                  'mb-2 !text-[24px] font-bold': selectedTab === 'Potential',
                }"
              >
                Truck: {{ truck?.carrier_vehicle?.id }}
              </p>
              <p class="text-xs font-normal text-steel-700">
                VIN: {{ truck.carrier_vehicle?.vehicle?.vin || '-' }}
              </p>
              <p
                v-if="selectedTab !== 'Potential'"
                class="text-xs font-normal text-steel-700"
              >
                {{ dayjs(truck.start_date).format('MMMM DD') }}
              </p>

              <div
                v-if="selectedTab !== 'Potential'"
                class="my-3 flex flex-col gap-2"
              >
                <div class="flex items-center gap-1">
                  <img
                    class="mx-1 h-4 w-4"
                    src="@/assets/origin-marker.svg"
                    alt="origin icon"
                  />

                  <p class="text-steel-900">
                    {{ truck.origin_search_display_name }}
                  </p>
                </div>

                <div class="flex items-center gap-1">
                  <img
                    src="@/assets/loads/destination-marker.svg"
                    alt="origin icon"
                  />

                  <p class="text-steel-900">
                    {{ truck.destination_search_display_name || '-' }}
                  </p>
                </div>
              </div>
              <div
                v-else
                class="my-3"
              >
                <p class="text-lg font-bold text-steel-900">
                  {{ truck.origin_search_display_name }}
                </p>
                <p class="text-base font-normal text-steel-800">
                  {{ truck.origin_area?.name || '' }}
                </p>
              </div>

              <p
                class="steel-900 w-max rounded-lg bg-steel-100 px-2 py-[2px] capitalize"
              >
                {{ truck.posted_equipment_type?.replace(/_/g, ' ') }}
              </p>

              <div
                v-if="selectedTab === 'Active'"
                class="mt-3 flex gap-3"
              >
                <ButtonComponent
                  class="flex h-11 flex-1 justify-center"
                  @click="toggleRemoveModal(truck)"
                >
                  Remove
                </ButtonComponent>
                <ButtonComponent
                  variant="secondary"
                  class="flex h-11 flex-1 justify-center"
                  @click="toggleEditModal(truck)"
                >
                  Edit
                </ButtonComponent>
              </div>

              <div
                v-else-if="selectedTab === 'Expired'"
                class="mt-3 flex [&>*]:flex-1"
              >
                <ButtonComponent
                  v-if="canTruckRepost(truck)"
                  variant="secondary"
                  @click="toggleRepostTruckModal(truck)"
                >
                  Repost
                </ButtonComponent>
              </div>

              <div
                v-if="selectedTab === 'Potential'"
                class="capacity-checkbox absolute right-2 top-3"
              >
                <CheckboxButton
                  item=""
                  :active="selectedPotentialCapacities?.includes(truck?.id)"
                  :handle-select="
                    () => emit('handle-select-potential-capacity', truck?.id)
                  "
                  class="!h-6 !py-0"
                />
              </div>
            </div>
          </div>

          <InfiniteScroll
            class="w-full"
            @load-more="loadMoretrucks"
          ></InfiniteScroll>

          <div
            v-if="isTrucksRefetching && !is_truck_posting_loading"
            class="pb-12 pt-6"
          >
            <img
              class="loading m-auto h-12 w-12"
              src="@/assets/loading-v2-yellow.svg"
              alt="loading spinner"
            />
          </div>
        </div>
        <div
          v-else
          class="mt-4"
        >
          <div
            v-if="selectedTab === 'Potential'"
            class="mt-6 flex h-full flex-col items-center"
          >
            <img
              src="@/assets/empty-suggested-capacity.svg"
              alt="empty capacity"
            />
            <p class="text-lg font-normal text-steel-700">
              There is no suggested capacity to display
            </p>
          </div>

          <p
            v-else
            class="text-lg font-normal text-steel-700"
          >
            {{
              isExpired
                ? 'You have no expired trucks at this time.'
                : 'You have no posted trucks at this time.'
            }}
          </p>
        </div>
      </div>
    </div>

    <remove-capacity-modal
      v-if="isRemoveModalVisible"
      :visible="isRemoveModalVisible"
      :capacity="selectedCapacity"
      @close="toggleRemoveModal"
    ></remove-capacity-modal>

    <post-capacity-modal
      v-if="isEditModalVisible"
      :visible="isEditModalVisible"
      :is-edit="true"
      :is-reposting="isRepostingTruck"
      :capacity="selectedCapacity"
      @close="toggleEditModal"
      @handle-tab-selection="emit('handle-tab-selection')"
    ></post-capacity-modal>

    <PotentialCapacityDrawer
      v-if="showCapacityDrawer"
      v-model="showCapacityDrawer"
      :capacity="selectedCapacity"
      @close="toggleCapacityDrawer"
      @handle-post-suggested-capacities="
        emit('handle-post-suggested-capacities', $event)
      "
    ></PotentialCapacityDrawer>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import Popper from 'vue3-popper';
import { useToast } from 'vue-toast-notification';
import {
  ButtonComponent,
  DataTable,
  InfiniteScroll,
  CheckboxButton,
  CapacityNotification,
  PostCapacityModal,
  RemoveCapacityModal,
  PotentialCapacityDrawer,
  SingleToggleIcon as ToggleIcon,
} from '@/components';
import { createFilterQuery } from '@/utils';

const store = useStore();
const router = useRouter();
const toast = useToast();

const props = defineProps({
  isDesktop: {
    type: Boolean,
    default: false,
  },
  selectedTab: {
    type: String,
  },
  selectedPotentialCapacities: {
    type: Array,
  },
  isAllAvailablePotentialPostingsSelected: {
    type: Boolean,
    default: false,
  },
  availablePotentialPostings: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'handle-tab-selection',
  'handle-select-potential-capacity',
  'handle-select-all-potential-capacities',
  'handle-post-suggested-capacities',
]);

const isRemoveModalVisible = ref(false);
const selectedCapacity = ref({});

const showPotentialCapacityTab = computed(
  () => store.state.users.featureFlags?.fe_potential_capacities?.enabled
);

const requireTruckNumberForPosting = computed(
  () =>
    store.state.users.featureFlags?.fe_require_truck_number_for_posting?.enabled
);

const handleSelectMobileSuggested = (truck) => {
  if (props?.selectedTab === 'Potential') {
    handleEditPotentialCapacity(truck);
  }
};

const truckPostingsLength = computed(() => {
  const length = truck_postings.value?.length;

  if (length > 99) {
    return '99+';
  }
  return length;
});

const potentialTrucksLength = computed(() => {
  const length = potential_trucks.value?.length;

  if (length > 99) {
    return '99+';
  }
  return length;
});

const isExpired = computed(() => props?.selectedTab === 'Expired');

const is_truck_posting_loading = computed(() => {
  if (isExpired.value) {
    return store.state.loads.is_expired_truck_posting_loading;
  } else if (props?.selectedTab === 'Potential') {
    return store.state.loads.is_potential_truck_posting_loading;
  } else {
    return store.state.loads.is_truck_postings_loading;
  }
});

const isPotentialCapacityAutomatic = computed(
  () => store.state.brokers?.company?.is_potential_capacity_automatic
);

const canUpdateAutomaticCapacitySettings = computed(
  () =>
    store.state.users.currentUser?.scopes?.includes(
      'carrier_view_posted_loads'
    ) && props?.selectedTab === 'Potential'
);

const automatically_post_capacities = ref(undefined);

let initialized = false;
watch(
  isPotentialCapacityAutomatic,
  (newVal) => {
    automatically_post_capacities.value = newVal;

    if (props?.selectedTab !== 'Potential') {
      initialized = true;
    }
  },
  { immediate: true }
);

const handleCapacitySettings = async () => {
  if (!initialized) {
    initialized = true;
    return;
  }
  const automatticSettingsValue = !automatically_post_capacities.value;
  automatically_post_capacities.value = automatticSettingsValue;

  const res = await store.dispatch('brokers/togglePotentialCapacityAutomatic', {
    company_id: store.state.brokers?.company?.id,
    is_potential_capacity_automatic: automatticSettingsValue,
  });

  if ([200, 201].includes(res?.status)) {
    toast.open({
      message: 'Potential capacity automatic settings updated.',
      position: 'top-right',
      type: 'success',
      className:
        'v-toast__item v-toast__item--success v-toast__item--top-right [&>p]:!max-w-fit',
      duration: 5000,
    });
  } else {
    toast.open({
      message:
        'Failed to update potential capacity automatic settings. Please try again',
      position: 'top-right',
      type: 'error',
      duration: 5000,
    });
  }
};

const toggleRemoveModal = (truck) => {
  isRemoveModalVisible.value = !isRemoveModalVisible.value;

  if (truck) {
    selectedCapacity.value = truck;
  } else {
    selectedCapacity.value = {};
  }
};

const isRepostingTruck = ref(false);
const isEditModalVisible = ref(false);
const toggleEditModal = (truck) => {
  isEditModalVisible.value = !isEditModalVisible.value;
  isRepostingTruck.value = false;

  if (truck) {
    selectedCapacity.value = truck;
  } else {
    selectedCapacity.value = {};
  }
};

const toggleRepostTruckModal = (truck) => {
  isEditModalVisible.value = !isEditModalVisible.value;
  isRepostingTruck.value = true;

  if (truck) {
    selectedCapacity.value = truck;
  } else {
    selectedCapacity.value = {};
  }
};

const canTruckRepost = (truck) => {
  return !truck_postings.value?.find(
    (t) => t.carrier_vehicle_id === truck.carrier_vehicle_id
  );
};

const showCapacityDrawer = ref(false);

const toggleCapacityDrawer = () => {
  showCapacityDrawer.value = !showCapacityDrawer.value;
};

const handleEditPotentialCapacity = (capacity) => {
  selectedCapacity.value = capacity;
  toggleCapacityDrawer();
};

const truck_postings = computed(() => store.state.loads?.truck_postings);
const truck_postings_pagination = computed(
  () => store.state.loads?.truck_postings_pagination
);

const expired_trucks = computed(
  () => store.state.loads?.expired_truck_postings
);
const expired_truck_pagination = computed(
  () => store.state.loads?.expired_truck_postings_pagination
);

const potential_trucks = computed(
  () => store.state.loads?.potential_truck_postings
);
const potential_truck_pagination = computed(
  () => store.state.loads?.potential_truck_postings_pagination
);

const trucks = computed(() => {
  if (isExpired.value) {
    return expired_trucks?.value;
  } else if (props?.selectedTab === 'Potential') {
    return potential_trucks?.value;
  } else {
    return truck_postings?.value;
  }
});

const truck_pagination = computed(() => {
  if (isExpired.value) {
    return expired_truck_pagination?.value;
  } else if (props?.selectedTab === 'Potential') {
    return potential_truck_pagination?.value;
  } else {
    return truck_postings_pagination?.value;
  }
});

const isTrucksRefetching = ref(false);

const loadMoretrucks = async () => {
  console.log('Hit infinite scroll');

  if (isExpired?.value) {
    if (expired_truck_pagination?.value?.totalRecords > trucks?.value?.length) {
      isTrucksRefetching.value = true;

      await store.dispatch('loads/getExpiredTruckPostings', {
        page: expired_truck_pagination?.value?.currentPage + 1,
        refetch: true,
      });
    }
    isTrucksRefetching.value = false;
  } else if (props?.selectedTab === 'Potential') {
    if (
      potential_truck_pagination?.value?.totalRecords > trucks?.value?.length
    ) {
      isTrucksRefetching.value = true;

      await store.dispatch('loads/getPotentialTruckPostings', {
        page: potential_truck_pagination?.value?.currentPage + 1,
        refetch: true,
      });
    }
  } else {
    if (
      truck_postings_pagination?.value?.totalRecords > trucks?.value?.length
    ) {
      isTrucksRefetching.value = true;

      await store.dispatch('loads/getTruckPostings', {
        query: '?q[is_active_eq]=1',
        page: truck_postings_pagination?.value?.currentPage + 1,
        refetch: true,
        quiet: true,
      });
    }
  }
};

const onSelectPage = (page) => {
  if (isExpired?.value) {
    store.dispatch('loads/getExpiredTruckPostings', {
      page,
    });
  } else {
    store.dispatch('loads/getTruckPostings', {
      query: '?q[is_active_eq]=1',
      page,
    });
  }
};

const handleTruckSelection = async (truck) => {
  if (!isExpired?.value) {
    const searchObject = {
      ...truck,
      origin: truck?.origin_search_display_name,
      destination: truck?.destination_search_display_name,
      selectedOrigin: truck?.origin_area_id,
      selectedDestination: truck?.destination_area_id,
      trailer: [truck?.posted_equipment_type],
      pickup_start: truck?.start_date,
      pickup_end: dayjs(truck?.start_date).add(1, 'day').format('YYYY-MM-DD'),
      origin_deadhead: truck?.origin_deadhead_tolerance,
      destination_deadhead: truck?.destination_deadhead_tolerance,
    };

    await store.commit('loads/setLoads', []);
    await store.commit('loads/setLoadSearchFilters', searchObject);

    await store.dispatch('toast/addToast', {
      component: CapacityNotification,
      props: {
        visible: true,
        title: 'Viewing loads matching capacity for',
        truck,
        isLoadBoardView: true,
      },
    });

    const filterParams = createFilterQuery(
      {
        origin: truck?.origin_area?.name,
        destination: truck?.destination_area?.name,
        selectedOrigin: truck?.origin_area?.name,
        selectedDestination: truck?.destination_area?.name,
        trailer: [truck?.posted_equipment_type],
        pickup_start: truck?.start_date,
        pickup_end: dayjs(truck?.start_date).add(1, 'day').format('YYYY-MM-DD'),
      },
      {}
    );

    await router.push({
      name: 'Load Board',
      query: filterParams,
    });
  }
};
</script>

<style>
.capacity-checkbox .checkbox-checkmark {
  @apply h-5 w-5 border-2 border-steel-300;
}

.capacity-checkbox .container .checkbox-checkmark:after {
  @apply border-black;
  width: 5px;
  height: 9px;
  border-width: 0 2px 2px 0;
  top: 4px !important;
  left: 7px !important;
}

.capacity-checkbox .container input:checked ~ .checkbox-checkmark {
  @apply border border-transparent;
}

.capacity-checkbox .container {
  @apply py-2 pl-9 pr-0;
}

.capacity-table .selected-row-item {
  @apply bg-sun-50;
}
</style>
